import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import { Link } from 'react-router-dom';
import MuiLink from '@mui/material/Link';
import PfIcon from './assets/icons/PfIcon';
import PfIconRev from './assets/icons/PfIconRev';
import SpotifyIcon from './assets/icons/SpotifyIcon';
import AppleIcon from '@mui/icons-material/Apple';
import { IconButton, Tooltip } from '@mui/material';

const INITIAL_YOUTUBE_VIDEO_POSITION = 16; // Lamer

function Home() {
  const { t } = useTranslation();
  const youtubeContentCarousel = [
    // 0 - Breaking Bad - Casa Del Popolo May 2024
    'JgSgu-i08U8',
    // 1 - Namor - Casa Del Popolo May 2024
    'P-uaKcg4UtU',
    // 2 - Red Rum - Casa Del Popolo May 2024
    'ghMhWDFonkY',
    // 3 - Broken Clouds - Casa Del Popolo May 2024
    'Z_3oWEN90lM',
    // 4 - Broken Clouds Visualizer
    'BGNUMQ2XTKU',
    // 5 - Thanos 1 + 2 Visualizer
    '6hX2KG9OFCY',
    // 6 - Thanos 3 Visualizer
    'gqUnt-_UXyc',
    // 7 - Thanos 4 Visualizer
    'ymGmzQj4_G8',
    // 8 - Thanos 5 Visualizer
    'HOhp9_D0gM8',
    // 9 - Thanos 6 Visualizer
    't5ptPdXS5Zs',
    // 10 - Breaking Bad Visualizer
    'M9S8LsqPSCM',
    // 11 - Lullaby in V Visualizer
    'gKkjkgTkX9U',
    // 12 - Namor Visualizer
    'zxIUMRQ9u90',
    // 13 - Thanos 6 - Freq Shop October 2024
    'edSXG8aRcdw',
    // 14 - Stu Korg
    '1LTTNCKm9rA',
    // 15 - Little Sunflower
    'Wu2hV7VRhIA',
    // 16 - Lamer
    'Ty478CShp_Q'
  ];

  const [
    youtubeContentCarouselPosition,
    setYoutubeContentCarouselPosition
  ] = useState(INITIAL_YOUTUBE_VIDEO_POSITION);

  const [playerBeyondReady, setPlayerBeyondReady] = useState(false);

  const youtubeIframeRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  // Track whether we've already appended the YT script
  const ytScriptLoadedRef = useRef(false);

  useEffect(() => {
    if (ytScriptLoadedRef.current) return;
    ytScriptLoadedRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onload = () => {
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnPlayerReady = (event) => {
    if (playerBeyondReady) {
      event.target.playVideo();
    } else {
      setPlayerBeyondReady(true);
    }
  };

  const initializePlayer = () => {
    if (youtubeIframeRef.current && window.YT) {
      youtubePlayerRef.current = new window.YT.Player(youtubeIframeRef.current, {
        videoId: youtubeContentCarousel[youtubeContentCarouselPosition],
        events: {
          onStateChange: handleYouTubeStateChange,
          onReady: handleOnPlayerReady,
        },
      });
    } else {
      console.error('Youtube not ready');
    }
  };

  const contentCarousel = (direction) => {
    if (direction) {
      setYoutubeContentCarouselPosition(prev =>
        prev < youtubeContentCarousel.length - 1 ? prev + 1 : 0
      );
    } else {
      setYoutubeContentCarouselPosition(prev =>
        prev === 0 ? youtubeContentCarousel.length - 1 : prev - 1
      );
    }
  };

  const getYoutubeIframeSource = () =>
    `https://www.youtube.com/embed/${youtubeContentCarousel[youtubeContentCarouselPosition]}?enablejsapi=1`;

  const handleYouTubeStateChange = (event) => {
    // 0 = ended → auto-advance
    if (event.data === 0) {
      contentCarousel(true);
    }
  };


  return (
    <Container disableGutters={true} maxWidth={false} className='container'>

      {/* ── Lamer banner ── */}
      <Box className='banner'>
        <h3 className='bannerRow'>
          <MuiLink href='https://open.spotify.com/album/0RfO9w6egmMJRgOe75oelO?si=DBs2906wR3CH4hHSrTTaww' target='_blank' className='noDeco bannerRowIcon'>
            <SpotifyIcon />
          </MuiLink>
          <Link to='/music' className='noDeco'>
            {t('home.new_music_lamer')}
          </Link>
          <MuiLink href='https://music.apple.com/us/album/lamer-single/6811264463' target='_blank' className='noDeco bannerRowIcon'>
            <AppleIcon />
          </MuiLink>
        </h3>
      </Box>

      {/* ── YouTube carousel ── */}
      <Box className='youtubeCarousel'>
        <Box className='youtubeWrapper'>
          {/* Prev button — overlaid on the left edge of the video */}
          <Tooltip arrow title={t('navigation.previous')} placement='right'>
            <IconButton
              className='youtubeCarrouselButtonL'
              onClick={() => contentCarousel(false)}
              size='large'
            >
              <PfIconRev />
            </IconButton>
          </Tooltip>

          <iframe
            ref={youtubeIframeRef}
            id='YouTubeContent'
            src={getYoutubeIframeSource()}
            title='YouTube video player'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen;'
            frameBorder='0'
            referrerPolicy='strict-origin-when-cross-origin'
            allowFullScreen
            onLoad={initializePlayer}
          />

          {/* Next button — overlaid on the right edge of the video */}
          <Tooltip arrow title={t('navigation.next')} placement='left'>
            <IconButton
              className='youtubeCarrouselButtonR'
              onClick={() => contentCarousel(true)}
              size='large'
            >
              <PfIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Dot indicators ── */}
      <Box className='youtubeDots' role='tablist' aria-label='video selector'>
        {youtubeContentCarousel.map((_, i) => (
          <span
            key={i}
            role='tab'
            aria-selected={i === youtubeContentCarouselPosition}
            aria-label={`Video ${i + 1}`}
            className={`youtubeDot${i === youtubeContentCarouselPosition ? ' youtubeDot--active' : ''}`}
            onClick={() => setYoutubeContentCarouselPosition(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* ── Upcoming Show banner ── */}
      <Box className='banner'>
        {/* <Link to='/store' className='noDeco'> */}
          <h3><a href='https://www.facebook.com/share/1LbxGYN7A4/' rel='noreferrer' target='_blank' className='noDeco'>{t('home.next_show')}<PfIcon />{t('home.upcoming_show')}</a></h3>
        {/* </Link> */}
      </Box>


      {/* ── Instagram photo section ── */}
      <Box className='bgimg5'>
        <Box className='caption'>
          <a href='https://www.instagram.com/pushforward.online' rel='noreferrer' target='_blank' className='noDeco'>
            <span className='captionPill'>
              <InstagramIcon className='noDeco bannerIcon' />
              <span className='noDeco bannerMessage'>{t('home.follow_insta')}</span>
            </span>
          </a>
        </Box>
      </Box>

{/* ── Top music CTA banner ── */}
      <Box className='banner'>
        <h3 className='bannerRow'>
          <MuiLink href='https://open.spotify.com/artist/4WQRHx9X2yrzDSTkbKUvTE?si=edPFNsoPQwmAmXmvyW1QqA' target='_blank' className='noDeco bannerRowIcon'>
            <SpotifyIcon />
          </MuiLink>
          <Link to='/music' className='noDeco'>
            {t('home.new_music')} <PfIcon className='bannerInlineIcon' /> {t('music.available_now')}
          </Link>
          <MuiLink href='https://music.apple.com/us/artist/push-forward/1746204663' target='_blank' className='noDeco bannerRowIcon'>
            <AppleIcon />
          </MuiLink>
        </h3>
      </Box>

      {/* ── Facebook photo section ── */}
      <Box className='bgimg4'>
        <Box className='caption'>
          <a href='https://www.facebook.com/profile.php?id=61558005543750' rel='noreferrer' target='_blank' className='noDeco'>
            <span className='captionPill'>
              <FacebookIcon className='noDeco bannerIconBlue' />
              <span className='noDeco bannerMessage'>{t('home.like_fb')}</span>
            </span>
          </a>
        </Box>
      </Box>

      {/* ── Merch banner ── */}
      <Box className='banner'>
        <Link to='/store' className='noDeco'>
          <h3>{t('home.merch')} <PfIcon /> {t('home.available_at_show')}</h3>
        </Link>
      </Box>

      {/* ── About photo section ── */}
      <Box className='bgimg1'>
        <Box className='caption'>
          <Link to='/about' className='noDecoB'>
            <span className='captionPill' style={{ background: 'rgba(230,225,219,0.72)', backdropFilter: 'blur(4px)' }}>
              <PfIcon className='noDecoB bannerIconB' />
              <span className='noDecoB bannerMessageB'>
                {t('home.learn_more_about')}
              </span>
            </span>
          </Link>
        </Box>
      </Box>

    </Container>
  );
}

export default Home;
